import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Briefcase, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ApplicationCard } from "../components/applications/ApplicationCard";
import { ApplicationForm } from "../components/applications/ApplicationForm";
import { STATUS_META } from "../components/applications/status";

const PIPELINE = ["researching", "preparing", "submitted", "interview", "offer"];

export default function Applications() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | application
  const [filter, setFilter] = useState("active");

  const load = async () => { try { const { data } = await api.get("/applications"); setData(data); } catch (err) { console.error("[ARIA]", err); } };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing === "new") await api.post("/applications", form);
      else await api.put(`/applications/${editing.application_id}`, form);
      toast.success(editing === "new" ? "Application added" : "Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const patch = async (app, body) => {
    try { await api.put(`/applications/${app.application_id}`, body); load(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (app) => {
    if (!confirm(`Delete "${app.title}"?`)) return;
    await api.delete(`/applications/${app.application_id}`); load();
  };

  if (!data) return <div className="p-8 text-[var(--aria-text-muted)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  const apps = data.applications;
  const closed = new Set(["accepted", "rejected", "withdrawn"]);
  const visible = apps.filter(a => filter === "all" || (filter === "active" ? !closed.has(a.status) : a.status === filter));
  const next = apps.filter(a => a.deadline && !closed.has(a.status) && new Date(a.deadline) >= new Date()).sort((a, b) => a.deadline.localeCompare(b.deadline))[0];
  const daysTo = (iso) => Math.ceil((new Date(iso) - new Date()) / 86400000);

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2"><Briefcase size={22} className="text-[var(--aria-primary)]" /><h1 className="font-display text-3xl">Applications</h1></div>
            <p className="text-sm text-[var(--aria-text-muted)] mt-1">PhD, postdoc, fellowship and job applications — deadlines land on your Calendar automatically.</p>
          </div>
          <button onClick={() => setEditing("new")} data-testid="new-application-button" className="btn btn-primary px-4 py-2 text-sm flex items-center gap-2"><Plus size={14} /> Add application</button>
        </div>

        <div className="grid sm:grid-cols-5 gap-2 mb-6" data-testid="application-pipeline">
          {PIPELINE.map(s => (
            <button key={s} onClick={() => setFilter(filter === s ? "active" : s)} data-testid={`pipeline-${s}`}
              className={`aria-card p-3 text-left transition-colors ${filter === s ? "ring-1 ring-[var(--aria-primary)]" : ""}`}>
              <div className="text-2xl font-display">{data.counts[s] || 0}</div>
              <div className={`text-[10px] uppercase tracking-[0.18em] mt-0.5 ${STATUS_META[s].cls.split(" ")[0]}`}>{STATUS_META[s].label}</div>
            </button>
          ))}
        </div>

        {next && (
          <div className="aria-card p-4 mb-6 flex items-center justify-between gap-3" data-testid="next-deadline-card">
            <div className="text-sm"><span className="text-[var(--aria-text-muted)]">Next due:</span> <span className="font-medium">{next.title}</span>{next.institution ? ` — ${next.institution}` : ""}</div>
            <span className={`aria-pill text-xs ${daysTo(next.deadline) <= 7 ? "text-red-400 border-red-400/40" : ""}`}>{daysTo(next.deadline)} day{daysTo(next.deadline) === 1 ? "" : "s"}</span>
          </div>
        )}

        {editing && <ApplicationForm initial={editing === "new" ? null : editing} types={data.types} statuses={data.statuses} onSave={save} onCancel={() => setEditing(null)} />}

        <div className="flex items-center gap-3 mb-3 text-xs text-[var(--aria-text-muted)]">
          {["active", "all", "accepted", "rejected"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`} className={`capitalize ${filter === f ? "text-[var(--aria-primary)]" : "hover:text-[var(--aria-text)]"}`}>{f}</button>
          ))}
        </div>

        <div className="space-y-3" data-testid="application-list">
          {visible.map(a => <ApplicationCard key={a.application_id} app={a} statuses={data.statuses} onEdit={() => setEditing(a)} onPatch={(body) => patch(a, body)} onDelete={() => remove(a)} />)}
          {visible.length === 0 && !editing && (
            <div className="text-center py-16 text-[var(--aria-text-muted)]" data-testid="applications-empty">No applications here yet — add the first one.</div>
          )}
        </div>
      </div>
    </div>
  );
}
